// AI Image Search — semantic search over the product-image catalog (admins only).
// Backed by ImageSearchController → the ClickHouse gemini-embedding-2 vectors
// (159K product images / 37K design-colorways). Three tabs:
//   Find Products   — describe what you want in plain language, in any language.
//                     Optional category + rug-size-interchange filters.
//   Room Scenes     — search AI-generated room scenes (ROOMAI) by text, or find
//                     scenes similar to the ones featuring a given item.
//   Identify Photo  — upload a photo, get candidate designs. SUGGESTIONS ONLY
//                     (~80% top-5 hit rate at catalog scale) — always confirm
//                     visually before using a match.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Paper, Typography, Stack, Chip, Button, TextField, MenuItem,
  CircularProgress, Alert, Tabs, Tab, Tooltip, InputAdornment, IconButton,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  MdSearch, MdImageSearch, MdChair, MdClose, MdFileUpload, MdOpenInNew,
} from 'react-icons/md';
import InterfaceHeader from '../components/InterfaceHeader';
import { TOOL_COLORS } from '../constants/colors';
import API_BASE_URL, { apiCall } from '../config/api';

const AI_COLOR = TOOL_COLORS.AI.primary;
const API = `${API_BASE_URL}/api/ImageSearch`;

const BAND_STYLES = {
  likely: { label: 'Likely match', color: '#2e7d32' },
  possible: { label: 'Possible match', color: '#ed6c02' },
  weak: { label: 'Weak match', color: '#757575' },
};

// One result card, shared by all three tabs. `badge` is the score/band chip.
const ResultCard = ({ thumb, image, title, subtitle, badge, sizes, attributes, alsoSoldAs, theme }) => (
  <Paper
    variant="outlined"
    sx={{
      width: 210, display: 'flex', flexDirection: 'column', overflow: 'hidden',
      borderRadius: 2, transition: 'box-shadow .15s', '&:hover': { boxShadow: 4 },
    }}
  >
    <Box
      component="a" href={image} target="_blank" rel="noopener noreferrer"
      sx={{
        display: 'block', height: 190, background: theme.palette.mode === 'dark' ? '#2a2a2a' : '#f7f5f2',
        position: 'relative',
      }}
    >
      <Box
        component="img" src={thumb} alt={title} loading="lazy"
        onError={(e) => { if (e.target.src !== image) e.target.src = image; }}
        sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
      />
      <MdOpenInNew style={{ position: 'absolute', top: 6, right: 6, opacity: 0.45 }} />
    </Box>
    <Box sx={{ p: 1.25, pt: 1 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>{title}</Typography>
        {badge}
      </Stack>
      {subtitle && (
        <Typography variant="caption" color="text.secondary" noWrap display="block">{subtitle}</Typography>
      )}
      {alsoSoldAs && alsoSoldAs.length > 0 && (
        <Tooltip title="Same product photos under other collection codes">
          <Typography variant="caption" color="text.secondary" noWrap display="block">
            also sold as {alsoSoldAs.join(', ')}
          </Typography>
        </Tooltip>
      )}
      {sizes && sizes.length > 0 && (
        <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {sizes.slice(0, 6).map((s) => (
            <Chip key={s} label={s} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
          ))}
          {sizes.length > 6 && (
            <Tooltip title={sizes.slice(6).join(', ')}>
              <Chip label={`+${sizes.length - 6}`} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
            </Tooltip>
          )}
        </Box>
      )}
      {attributes && (
        <Tooltip title={attributes} placement="top">
          <Typography
            variant="caption" color="text.secondary"
            sx={{
              mt: 0.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
              overflow: 'hidden', lineHeight: 1.35,
            }}
          >
            {attributes}
          </Typography>
        </Tooltip>
      )}
    </Box>
  </Paper>
);

const AIImageSearch = () => {
  const theme = useTheme();
  const [tab, setTab] = useState(0);
  const [meta, setMeta] = useState({ categories: [], sizeGroups: [] });

  // -- Find Products state
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('Rugs');
  const [sizeToken, setSizeToken] = useState('');
  const [products, setProducts] = useState(null);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState('');

  // -- Room Scenes state
  const [sceneQuery, setSceneQuery] = useState('');
  const [sceneItem, setSceneItem] = useState('');
  const [scenes, setScenes] = useState(null);
  const [scenesLoading, setScenesLoading] = useState(false);
  const [scenesError, setScenesError] = useState('');

  // -- Identify state
  const [idFile, setIdFile] = useState(null);
  const [idPreview, setIdPreview] = useState('');
  const [idResult, setIdResult] = useState(null);
  const [idLoading, setIdLoading] = useState(false);
  const [idError, setIdError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [idCategory, setIdCategory] = useState('Rugs');
  const fileInputRef = useRef(null);

  useEffect(() => {
    apiCall(`${API}/meta`)
      .then((data) => setMeta({ categories: data.categories || [], sizeGroups: data.sizeGroups || [] }))
      .catch(() => {}); // filters degrade gracefully to free-text
  }, []);

  const searchProducts = useCallback(async () => {
    if (!query.trim()) return;
    setProductsLoading(true);
    setProductsError('');
    try {
      const params = new URLSearchParams({ query: query.trim(), k: '24' });
      if (category) params.set('category', category);
      if (sizeToken) params.set('sizeToken', sizeToken);
      setProducts(await apiCall(`${API}/products?${params}`));
    } catch (err) {
      setProductsError(err.message || 'Search failed');
    } finally {
      setProductsLoading(false);
    }
  }, [query, category, sizeToken]);

  const searchScenes = useCallback(async () => {
    if (!sceneQuery.trim() && !sceneItem.trim()) return;
    setScenesLoading(true);
    setScenesError('');
    try {
      const params = new URLSearchParams({ k: '18' });
      if (sceneItem.trim()) params.set('similarToItem', sceneItem.trim());
      else params.set('query', sceneQuery.trim());
      setScenes(await apiCall(`${API}/scenes?${params}`));
    } catch (err) {
      setScenesError(err.message || 'Search failed');
    } finally {
      setScenesLoading(false);
    }
  }, [sceneQuery, sceneItem]);

  const acceptFile = useCallback((file) => {
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) {
      setIdError('Use a jpeg, png or webp image.');
      return;
    }
    if (file.size > 12_000_000) {
      setIdError('Image is larger than 12 MB — use a smaller copy.');
      return;
    }
    setIdError('');
    setIdResult(null);
    setIdFile(file);
    setIdPreview(URL.createObjectURL(file));
  }, []);

  const identify = useCallback(async () => {
    if (!idFile) return;
    setIdLoading(true);
    setIdError('');
    try {
      const form = new FormData();
      form.append('file', idFile);
      form.append('k', '8');
      if (idCategory) form.append('category', idCategory);
      setIdResult(await apiCall(`${API}/identify`, { method: 'POST', body: form }));
    } catch (err) {
      setIdError(err.message || 'Identification failed');
    } finally {
      setIdLoading(false);
    }
  }, [idFile, idCategory]);

  const scoreChip = (value, color) => (
    <Chip
      label={value.toFixed(2)} size="small"
      sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700, color: '#fff', bgcolor: color || AI_COLOR }}
    />
  );

  const resultsGrid = (children) => (
    <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>{children}</Box>
  );

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
      <InterfaceHeader title="AI Image Search" color={AI_COLOR} category="ai" showFavorite={true} />
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
        Semantic search over {`${(159102).toLocaleString()}`} product images — describe what you want
        in plain language (any language, typos welcome), find AI room scenes, or identify a product
        from a photo.
      </Typography>

      <Tabs
        value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, '& .MuiTab-root': { textTransform: 'none' } }}
        TabIndicatorProps={{ style: { backgroundColor: AI_COLOR } }}
      >
        <Tab icon={<MdSearch />} iconPosition="start" label="Find Products" />
        <Tab icon={<MdChair />} iconPosition="start" label="Room Scenes" />
        <Tab icon={<MdImageSearch />} iconPosition="start" label="Identify from Photo" />
      </Tabs>

      {/* ---------------- Find Products ---------------- */}
      {tab === 0 && (
        <Box>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <TextField
                fullWidth size="small" autoFocus
                placeholder='e.g. "something boho for the living room, not too fancy" — English, Spanish, typos all fine'
                value={query} onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && searchProducts()}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><MdSearch /></InputAdornment>,
                }}
              />
              <TextField
                select size="small" label="Category" value={category}
                onChange={(e) => setCategory(e.target.value)} sx={{ minWidth: 190 }}
              >
                <MenuItem value="">All categories</MenuItem>
                {meta.categories.map((c) => (
                  <MenuItem key={c.category} value={c.category}>
                    {c.category} ({Number(c.designs).toLocaleString()})
                  </MenuItem>
                ))}
              </TextField>
              <Tooltip title="Rug size-interchange filter — only designs available in this size class. Use for replacement flows; leave off when browsing.">
                <TextField
                  select size="small" label="Size group" value={sizeToken}
                  onChange={(e) => setSizeToken(e.target.value)} sx={{ minWidth: 150 }}
                >
                  <MenuItem value="">Any size</MenuItem>
                  {meta.sizeGroups.map((s) => (
                    <MenuItem key={s.token} value={s.token}>
                      {s.token} ({Number(s.designs).toLocaleString()})
                    </MenuItem>
                  ))}
                </TextField>
              </Tooltip>
              <Button
                variant="contained" onClick={searchProducts}
                disabled={productsLoading || !query.trim()}
                sx={{ bgcolor: AI_COLOR, '&:hover': { bgcolor: TOOL_COLORS.AI.hover }, minWidth: 110 }}
              >
                {productsLoading ? <CircularProgress size={22} color="inherit" /> : 'Search'}
              </Button>
            </Stack>
          </Paper>

          {productsError && <Alert severity="error" sx={{ mt: 2 }}>{productsError}</Alert>}
          {products && products.lowConfidence && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Low confidence (best raw similarity {products.topRawSimilarity}) — the catalog may not
              have what you described. Check the images before using these results.
            </Alert>
          )}
          {products && products.shapeFilter && (
            <Alert severity="success" icon={false} sx={{ mt: 2 }}>
              Showing only designs that come in a <strong>{products.shapeFilter}</strong> size, with that shape's photo — because you said so in the search.
            </Alert>
          )}
          {products && products.results && products.results.length === 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>No results — try loosening the filters.</Alert>
          )}
          {products && resultsGrid(products.results.map((r) => (
            <ResultCard
              key={r.design} theme={theme} thumb={r.thumb} image={r.image}
              title={r.design} subtitle={r.exampleItem ? `e.g. ${r.exampleItem}` : null}
              badge={scoreChip(r.score)}
              sizes={r.sizes} attributes={r.attributes} alsoSoldAs={r.alsoSoldAs}
            />
          )))}
        </Box>
      )}

      {/* ---------------- Room Scenes ---------------- */}
      {tab === 1 && (
        <Box>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems="center">
              <TextField
                fullWidth size="small"
                placeholder='Describe a scene — "bright scandinavian living room with plants"'
                value={sceneQuery}
                onChange={(e) => { setSceneQuery(e.target.value); if (e.target.value) setSceneItem(''); }}
                onKeyDown={(e) => e.key === 'Enter' && searchScenes()}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><MdSearch /></InputAdornment>,
                }}
              />
              <Typography variant="body2" color="text.secondary">or</Typography>
              <TextField
                size="small" sx={{ minWidth: 220 }}
                placeholder="Scenes featuring item / design"
                value={sceneItem}
                onChange={(e) => { setSceneItem(e.target.value); if (e.target.value) setSceneQuery(''); }}
                onKeyDown={(e) => e.key === 'Enter' && searchScenes()}
              />
              <Button
                variant="contained" onClick={searchScenes}
                disabled={scenesLoading || (!sceneQuery.trim() && !sceneItem.trim())}
                sx={{ bgcolor: AI_COLOR, '&:hover': { bgcolor: TOOL_COLORS.AI.hover }, minWidth: 110 }}
              >
                {scenesLoading ? <CircularProgress size={22} color="inherit" /> : 'Search'}
              </Button>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Searches the AI-generated room scenes (ROOMAI images) — useful as generation backdrops
              and style references for the photo team.
            </Typography>
          </Paper>

          {scenesError && <Alert severity="error" sx={{ mt: 2 }}>{scenesError}</Alert>}
          {scenes && scenes.results && scenes.results.length === 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>No scenes found.</Alert>
          )}
          {scenes && resultsGrid(scenes.results.map((r) => (
            <ResultCard
              key={r.image} theme={theme} thumb={r.thumb} image={r.image}
              title={r.featuredDesign || r.item || 'Scene'}
              subtitle={r.item && r.item !== r.featuredDesign ? r.item : null}
              badge={scoreChip(r.score)}
            />
          )))}
        </Box>
      )}

      {/* ---------------- Identify from Photo ---------------- */}
      {tab === 2 && (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            Suggestions only — the right design appears in the top results about 80% of the time at
            full-catalog scale. Always confirm visually before using a match; black-and-white photos
            identify poorly.
          </Alert>
          <Paper
            variant="outlined"
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); acceptFile(e.dataTransfer.files[0]); }}
            sx={{
              p: 3, borderRadius: 2, textAlign: 'center', cursor: 'pointer',
              borderStyle: 'dashed', borderWidth: 2,
              borderColor: dragOver ? AI_COLOR : 'divider',
              bgcolor: dragOver ? `${AI_COLOR}10` : 'transparent',
              transition: 'all .15s',
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" hidden
              onChange={(e) => acceptFile(e.target.files[0])}
            />
            {idPreview ? (
              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                <Box component="img" src={idPreview} alt="upload preview"
                  sx={{ maxHeight: 260, maxWidth: '100%', borderRadius: 1 }} />
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIdFile(null); setIdPreview(''); setIdResult(null);
                  }}
                  sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'rgba(0,0,0,0.55)', color: '#fff' }}
                >
                  <MdClose />
                </IconButton>
              </Box>
            ) : (
              <Box sx={{ py: 3, color: 'text.secondary' }}>
                <MdFileUpload size={40} style={{ opacity: 0.6 }} />
                <Typography variant="body1" sx={{ mt: 1 }}>
                  Drop a product or room photo here, or click to choose one
                </Typography>
                <Typography variant="caption">jpeg / png / webp, up to 12 MB</Typography>
              </Box>
            )}
          </Paper>
          <Stack direction="row" spacing={1.5} sx={{ mt: 2 }} alignItems="center">
            <Tooltip title="What the photo shows. Matching across categories is unreliable, so this keeps furniture out of rug results (and vice versa).">
              <TextField
                select size="small" label="Photo shows" value={idCategory}
                onChange={(e) => setIdCategory(e.target.value)} sx={{ minWidth: 200 }}
              >
                <MenuItem value="">Anything (no filter)</MenuItem>
                {meta.categories.map((c) => (
                  <MenuItem key={c.category} value={c.category}>{c.category}</MenuItem>
                ))}
              </TextField>
            </Tooltip>
            <Button
              variant="contained" onClick={identify} disabled={!idFile || idLoading}
              sx={{ bgcolor: AI_COLOR, '&:hover': { bgcolor: TOOL_COLORS.AI.hover }, minWidth: 150 }}
            >
              {idLoading ? <CircularProgress size={22} color="inherit" /> : 'Identify'}
            </Button>
          </Stack>

          {idError && <Alert severity="error" sx={{ mt: 2 }}>{idError}</Alert>}
          {idResult && idResult.top1Confidence && (() => {
            const band = BAND_STYLES[idResult.top1Confidence] || BAND_STYLES.weak;
            const sev = idResult.top1Confidence === 'likely' ? 'success' : idResult.top1Confidence === 'possible' ? 'warning' : 'info';
            return (
              <Alert severity={sev} sx={{ mt: 2 }}>
                Top match: <strong>{band.label}</strong> — {idResult.calibration}. Margin to the runner-up {idResult.top1Margin}.
                {idResult.verifiedBy && ' The two closest designs were too close to call from the embedding alone, so a vision model compared the photos and picked this one.'}
              </Alert>
            );
          })()}
          {idResult && resultsGrid(idResult.candidates.map((r, i) => {
            const band = i === 0 ? (BAND_STYLES[idResult.top1Confidence] || BAND_STYLES.weak) : null;
            return (
              <ResultCard
                key={r.design} theme={theme} thumb={r.thumb} image={r.image}
                title={r.design} subtitle={i === 0 ? band.label : `#${i + 1}`}
                badge={scoreChip(r.similarity, band ? band.color : undefined)}
                sizes={r.sizes} attributes={r.attributes} alsoSoldAs={r.alsoSoldAs}
              />
            );
          }))}
        </Box>
      )}
    </Box>
  );
};

export default AIImageSearch;